/**
 * 名人案例详情落地页 /famous/<slug>。
 *
 * 每个历史人物一个独立可收录 URL，承载「姓名 + 生辰八字/四柱干支 + 五行分布」等
 * 可被百度抓取的正文，是名人长尾词（如「王阳明八字」）的落地页。
 * 会员专属案例（vipOnly）即使被直达也不展示干支，保持与列表页一致的付费口径。
 */
import type { MouseEvent } from "react";
import { findFamousBySlug } from "../data/famous";
import { legalNotice } from "../data/site";
import { onNavClick, routeToPath } from "../router";
import { PlaceholderPage } from "./PlaceholderPage";

const GAN_WUXING: Record<string, string> = {
  甲: "木", 乙: "木", 丙: "火", 丁: "火", 戊: "土",
  己: "土", 庚: "金", 辛: "金", 壬: "水", 癸: "水",
};
const ZHI_WUXING: Record<string, string> = {
  寅: "木", 卯: "木", 巳: "火", 午: "火", 辰: "土", 戌: "土",
  丑: "土", 未: "土", 申: "金", 酉: "金", 亥: "水", 子: "水",
};
const WUXING_ORDER = ["木", "火", "土", "金", "水"] as const;
const PILLAR_LABELS = ["年柱", "月柱", "日柱", "时柱"] as const;

function wuxingCount(tg: string, dz: string): Record<string, number> {
  const counter: Record<string, number> = { 木: 0, 火: 0, 土: 0, 金: 0, 水: 0 };
  for (const ch of tg) if (GAN_WUXING[ch]) counter[GAN_WUXING[ch]] += 1;
  for (const ch of dz) if (ZHI_WUXING[ch]) counter[ZHI_WUXING[ch]] += 1;
  return counter;
}

export function FamousDetailPage({
  slug,
  onNavigate,
}: {
  slug: string;
  onNavigate: (route: string) => void;
}) {
  const item = findFamousBySlug(slug);
  if (!item) return <PlaceholderPage title="未找到该案例" />;

  const nav = (route: string) => (e: MouseEvent<HTMLAnchorElement>) =>
    onNavClick(e, route, onNavigate);
  const pillars = PILLAR_LABELS.map((label, i) => ({
    label,
    gan: item.tg[i] ?? "",
    zhi: item.dz[i] ?? "",
  }));
  const counts = wuxingCount(item.tg, item.dz);

  return (
    <main className="page famous-detail-page">
      <nav className="breadcrumb" aria-label="面包屑">
        <a href="/" onClick={nav("home")}>首页</a>
        <span aria-hidden="true"> / </span>
        <a href={routeToPath("famous")} onClick={nav("famous")}>名人案例</a>
        <span aria-hidden="true"> / </span>
        <span aria-current="page">{item.name}</span>
      </nav>

      <h1>{item.name}八字 · 四柱干支命理资料</h1>
      <p className="famous-detail-meta">
        <span>{item.note}</span>
        <span>性别：{item.sex}</span>
        <span>分类：{item.category}</span>
      </p>

      {item.vipOnly ? (
        <section className="famous-detail-locked">
          <p>{item.name}的四柱干支为会员案例内容。</p>
          <button type="button" onClick={() => onNavigate("vip")}>前往会员中心查看</button>
        </section>
      ) : (
        <>
          <p className="famous-detail-intro">
            以下为{item.name}（{item.note}）的四柱八字干支资料，
            按年、月、日、时四柱排列，供传统文化爱好者对照学习与排盘练习参考。
          </p>

          <section className="famous-detail-pillars" aria-label={`${item.name}四柱八字`}>
            <table className="pillar-table">
              <thead>
                <tr>
                  {pillars.map((p) => (
                    <th key={p.label} scope="col">{p.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {pillars.map((p) => (
                    <td key={`g-${p.label}`}>
                      <span className="gz-char">{p.gan}</span>
                      <small>{GAN_WUXING[p.gan] ?? ""}</small>
                    </td>
                  ))}
                </tr>
                <tr>
                  {pillars.map((p) => (
                    <td key={`z-${p.label}`}>
                      <span className="gz-char">{p.zhi}</span>
                      <small>{ZHI_WUXING[p.zhi] ?? ""}</small>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </section>

          <section className="famous-detail-wuxing">
            <h2>五行分布</h2>
            <ul className="wuxing-list">
              {WUXING_ORDER.map((w) => (
                <li key={w}>
                  <span className="wuxing-name">{w}</span>
                  <span className="wuxing-num">{counts[w]}</span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}

      <p className="famous-detail-cta">
        想排自己的八字？前往
        <a href={routeToPath("paipan")} onClick={nav("paipan")}>四柱排盘工具</a>
        ，或回到<a href={routeToPath("famous")} onClick={nav("famous")}>名人案例</a>看更多历史人物。
      </p>

      <p className="paipan-notice">{legalNotice}</p>
    </main>
  );
}
